import { useEffect, useState } from "react";
import {
  TASK_KIND_LABELS,
  TASK_KIND_WAIT,
  type TaskKind,
  type TaskRuleView,
  type TaskTypeView,
} from "@dentacore/shared-types";

import {
  addTaskType,
  changeTaskType,
  listTaskRules,
  listTaskTypes,
  readAssignable,
  setTaskRule,
} from "../api/tasks";
import { useShown } from "../shell/locations";
import styles from "./tasks.module.css";

/** What raises each kind, and what closes it (TK-D13, TK-D34). */
const WHAT: Record<TaskKind, { raises: string; closes: string }> = {
  LOW_STOCK: {
    raises: "An item's stock left after bookings falls to its low mark. Only with Supply.",
    closes: "Closes when the stock is above the low mark again.",
  },
  UNPAID_BILL: {
    raises: "A bill is still unpaid this many days after it was issued.",
    closes: "Closes when the bill is paid in full or voided.",
  },
  RECALL_DUE: {
    raises: "A patient's recall date passes with nothing booked.",
    closes: "Closes when anything is booked for them, or the recall date changes.",
  },
  VISIT_NOT_CLOSED: {
    raises: "A visit is still open this many hours after it ended.",
    closes: "Closes when the visit is completed, marked a no-show or cancelled.",
  },
  BOOKING_NOT_CHECKED: {
    raises: "A booking made on WhatsApp is still not checked ten minutes later.",
    closes:
      "Closes when the booking is checked, cancelled or deleted. It is never cancelled for it.",
  },
};

/**
 * **Automatic tasks** (TK-D13, TK-D35): each kind on or off, the role it goes
 * to, and its wait. Off until switched on with a role (TK-D22, TK-D32);
 * switching on raises tasks only for what comes due afterwards (TK-D29).
 */
export function TaskRulesTab(): React.JSX.Element {
  const shown = useShown();
  const [rules, setRules] = useState<TaskRuleView[] | null>(null);
  const [roles, setRoles] = useState<{ id: string; name: string }[]>([]);
  const [drafts, setDrafts] = useState<
    Record<string, { roleId: string; wait: string; dentist: boolean; due: string }>
  >({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const at = shown.active ?? shown.ids[0];
    Promise.all([
      listTaskRules(controller.signal),
      at === undefined ? null : readAssignable(at, controller.signal),
    ])
      .then(([found, who]) => {
        setRules(found);
        setRoles(who?.roles ?? []);
        setDrafts(
          Object.fromEntries(
            found.map((r) => [
              r.kind,
              {
                roleId: r.role?.id ?? "",
                wait: r.waitAmount === null ? "" : String(r.waitAmount),
                dentist: r.toVisitDentist,
                due: r.dueDays === null ? "" : String(r.dueDays),
              },
            ]),
          ),
        );
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : "Could not read the settings");
      });
    return () => controller.abort();
  }, [shown.active, shown.key]);

  const save = (kind: TaskKind, enabled: boolean): void => {
    const draft = drafts[kind];
    if (draft === undefined) return;
    setError(null);
    setTaskRule(kind, {
      enabled,
      roleId: draft.roleId === "" ? null : draft.roleId,
      toVisitDentist: kind === "VISIT_NOT_CLOSED" && draft.dentist,
      waitAmount: TASK_KIND_WAIT[kind] === null || draft.wait === "" ? null : Number(draft.wait),
      dueDays: draft.due === "" ? null : Number(draft.due),
    })
      .then(setRules)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not save"),
      );
  };

  const edit = (
    kind: string,
    change: Partial<{ roleId: string; wait: string; dentist: boolean; due: string }>,
  ) =>
    setDrafts((was) => ({
      ...was,
      [kind]: { roleId: "", wait: "", dentist: false, due: "", ...was[kind], ...change },
    }));

  return (
    <div className={styles.settings} data-testid="task-rules">
      <p className={styles.lede}>
        Each kind is off until you switch it on and choose who gets its tasks. Switching one on
        raises tasks only for what comes due from then on.
      </p>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {rules?.map((rule) => {
        const draft = drafts[rule.kind] ?? { roleId: "", wait: "", dentist: false, due: "" };
        const unit = TASK_KIND_WAIT[rule.kind];
        return (
          <div key={rule.kind} className={styles.card} data-testid={`task-rule-${rule.kind}`}>
            <div>
              <strong>{TASK_KIND_LABELS[rule.kind]}</strong>
              <p className={styles.cardText}>{WHAT[rule.kind].raises}</p>
            </div>
            <button
              type="button"
              className={rule.enabled ? styles.ghost : styles.primary}
              onClick={() => save(rule.kind, !rule.enabled)}
              data-testid={`task-rule-switch-${rule.kind}`}
            >
              {rule.enabled ? "Switch off" : "Switch on"}
            </button>
            <div className={styles.cardMeta}>
              <label className={styles.line}>
                Give to
                <select
                  className={styles.filterSelect}
                  value={draft.roleId}
                  onChange={(e) => edit(rule.kind, { roleId: e.target.value })}
                  onBlur={() => rule.enabled && save(rule.kind, true)}
                  aria-label={`${TASK_KIND_LABELS[rule.kind]} goes to`}
                >
                  <option value="">Choose a role…</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </label>
              {rule.kind === "VISIT_NOT_CLOSED" && (
                <label className={styles.line}>
                  <input
                    type="checkbox"
                    checked={draft.dentist}
                    onChange={(e) => edit(rule.kind, { dentist: e.target.checked })}
                    onBlur={() => rule.enabled && save(rule.kind, true)}
                  />
                  The visit&rsquo;s dentist holds it; the role takes one with no dentist
                </label>
              )}
              {unit !== null && (
                <label className={styles.line}>
                  Wait
                  <input
                    type="number"
                    min={0}
                    max={365}
                    className={styles.number}
                    value={draft.wait}
                    onChange={(e) => edit(rule.kind, { wait: e.target.value })}
                    onBlur={() => rule.enabled && save(rule.kind, true)}
                    aria-label={`Wait in ${unit}`}
                  />
                  {unit}
                </label>
              )}
              {/*
                **Overdue after** (OQ-D9): blank, the default, never — an
                automatic task is ordered by when it was raised.
              */}
              <label className={styles.line}>
                Overdue after
                <input
                  type="number"
                  min={1}
                  max={365}
                  className={styles.number}
                  value={draft.due}
                  placeholder="never"
                  onChange={(e) => edit(rule.kind, { due: e.target.value })}
                  onBlur={() => rule.enabled && save(rule.kind, true)}
                  aria-label={TASK_KIND_LABELS[rule.kind] + " is overdue after this many days"}
                  data-testid={"task-rule-due-" + rule.kind}
                />
                days
              </label>
              <span>{WHAT[rule.kind].closes}</span>
              {rule.enabled && rule.enabledAt !== null && (
                <span>On since {new Date(rule.enabledAt).toLocaleDateString("en-GB")}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * **Task types** (TK-D14, TK-D35): the clinic's own manual types. Renamed in
 * place, retired rather than deleted — a retired type keeps its tasks and is
 * offered for no new one.
 */
export function TaskTypesTab({ onChanged }: { onChanged: () => void }): React.JSX.Element {
  const [types, setTypes] = useState<TaskTypeView[] | null>(null);
  const [adding, setAdding] = useState("");
  const [error, setError] = useState<string | null>(null);

  const reload = (): void => {
    listTaskTypes()
      .then(setTypes)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not read the types"),
      );
  };
  useEffect(reload, []);

  const change = (typeId: string, body: { name?: string; archived?: boolean }): void => {
    setError(null);
    changeTaskType(typeId, body)
      .then(() => {
        reload();
        onChanged();
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not save"),
      );
  };

  return (
    <div className={styles.settings} data-testid="task-types">
      <p className={styles.lede}>
        The kinds of task your team adds by hand. Automatic tasks have their own kinds and are not
        listed here.
      </p>
      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.list}>
        {types?.map((t) => (
          <div
            key={t.id}
            className={t.archived ? `${styles.typeRow} ${styles.retired}` : styles.typeRow}
          >
            <input
              className={styles.input}
              defaultValue={t.name}
              disabled={t.archived}
              aria-label="Type name"
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next !== "" && next !== t.name) change(t.id, { name: next });
              }}
            />
            <span className={styles.footNote}>
              {t.taskCount} task{t.taskCount === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              className={
                t.archived ? `${styles.ghost} ${styles.small}` : `${styles.danger} ${styles.small}`
              }
              onClick={() => change(t.id, { archived: !t.archived })}
            >
              {t.archived ? "Restore" : "Retire"}
            </button>
          </div>
        ))}
      </div>
      <form
        className={styles.addRow}
        onSubmit={(event) => {
          event.preventDefault();
          const name = adding.trim();
          if (name === "") return;
          setError(null);
          addTaskType(name)
            .then(() => {
              setAdding("");
              reload();
              onChanged();
            })
            .catch((cause: unknown) =>
              setError(cause instanceof Error ? cause.message : "Could not add it"),
            );
        }}
      >
        <input
          className={styles.input}
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          placeholder="A new type, e.g. Lab work"
          aria-label="New type"
          data-testid="task-type-new"
        />
        <button
          type="submit"
          className={styles.ghost}
          disabled={adding.trim() === ""}
          data-testid="task-type-add"
        >
          Add type
        </button>
      </form>
    </div>
  );
}
